import { slugify } from '@boss/utils';

import type { IEvent } from './server';

export interface ITrainingGroup {
  type: string;
  sessions: IEvent[];
}

const byDateAscending = (a: IEvent, b: IEvent): number => new Date(a.date).getTime() - new Date(b.date).getTime();

const compareGroupTypes = (a: ITrainingGroup, b: ITrainingGroup): number => {
  if (!a.type) {
    return b.type ? 1 : 0;
  }
  if (!b.type) {
    return -1;
  }

  return a.type.localeCompare(b.type);
};

/**
 * Groups upcoming training sessions into exactly one block per opleidingstype
 * (`event.type`), sessions sorted by date ascending. Sessions without a type
 * land in a single fallback group (empty `type`) that always comes last.
 */
export const groupTrainingsByType = (events: IEvent[]): ITrainingGroup[] => {
  const groups = new Map<string, IEvent[]>();

  for (const event of events) {
    const type = (event.type ?? '').trim();
    const sessions = groups.get(type) ?? [];

    sessions.push(event);
    groups.set(type, sessions);
  }

  return Array.from(groups.entries())
    .map(([type, sessions]) => ({ type, sessions: [...sessions].sort(byDateAscending) }))
    .sort(compareGroupTypes);
};

/**
 * Returns the sessions whose `event.type` slugifies to the requested topic.
 * Matching is case-insensitive; sessions without a type never match.
 */
export const filterTrainingsByType = (events: IEvent[], topic: string): IEvent[] => {
  const normalizedTopic = topic.trim().toLowerCase();

  return events.filter(event => {
    const type = (event.type ?? '').trim();

    return !!type && slugify(type.toLowerCase()) === normalizedTopic;
  });
};
